/**
 * 67.61 (1), the jungle look (the owner, Oct 19: *"I do want to be able to have density of forests that is jungle like,
 * not everywhere but just dialing up forest density"*): a dense wood is its trunks — the sim's, capped at `JUNGLE.trunkMul`
 * × today's packing (lane F: the held band's joins, ~25 a jungle cell against ~12, picked by forestCell itself so the same
 * cell gives the same trunks whoever asks) — and, over and under them, what is **drawn only**:
 *   - **the canopy closing**: crowns between the trunks at the canopy's height, so from below the sky is shut and from
 *     above the wood is one roof;
 *   - **the understory**: ferns and broad-leaved bushes between the trunks, chest high;
 *   - **vines**: hanging from the crowns.
 * The drawn layers still hide and slow (lane F (b): core/jungleDepth.ts, a per-cell depth field for sight and shots, behind
 * the jungle switch); here they are their look. `junglePieces` lays them over a stand's trunks (the sheet's stands and a
 * planet's, rendering/jungleField.ts, alike); each layer is one InstancedMesh, a few triangles a piece. Person units (a
 * person is 1.8). Presentation only.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';

/** The jungle's dials: trunks × today's (lane F's cap), their spacing ×, the drawn layers per trunk, the canopy fill's height (× the stand's mean tree height). */
export const JUNGLE = { trunkMul: 2, spacingMul: 0.72, canopyPerTrunk: 1.6, understoryPerTrunk: 3, vinesPerTrunk: 1.2, canopyAt: 0.78, crownScale: 1.2 };
/** The jungle's colours: deep greens, a wet dark bark. */
export const JUNGLE_COLORS = { bark: '#3e3428', leaf: '#3f6a34', leafDark: '#2a4a26', fern: '#4e8240', bush: '#365e2e', vine: '#2e4a24' };

export const JUNGLE_KINDS = ['canopy', 'fern', 'bush', 'vine'] as const;
export type JungleKind = (typeof JUNGLE_KINDS)[number];
export interface JungleTrunk { x: number; z: number; height: number }
export interface JungleLayers { group: THREE.Group; pieces: number; draws: number; triangles: number }
/** A drawn piece in its stand's frame (person units, y up): its kind, place, turn about up, scale and colour. */
export interface JunglePiece { kind: JungleKind; x: number; y: number; z: number; yaw: number; sx: number; sy: number; sz: number; color: number }

/** A crown blob: a squashed icosahedron (20 triangles). */
function crownGeo(): THREE.BufferGeometry { const g = new THREE.IcosahedronGeometry(1, 0); g.scale(1, 0.55, 1); return g; }
/** A fern: six fronds fanning out and down from the middle (12 triangles). */
function fernGeo(): THREE.BufferGeometry {
  const pos: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2, c = Math.cos(a), s = Math.sin(a), w = 0.18;
    const base = [0, 0.15, 0], tip = [c * 1.1, 0.35, s * 1.1], mid = [c * 0.55, 0.62, s * 0.55];
    pos.push(...base, mid[0] - s * w, mid[1], mid[2] + c * w, ...tip, ...base, ...tip, mid[0] + s * w, mid[1], mid[2] - c * w);
  }
  const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.computeVertexNormals(); return g;
}
/** A bush: a low dodecahedron (36 triangles). */
function bushGeo(): THREE.BufferGeometry { const g = new THREE.DodecahedronGeometry(1, 0); g.scale(1, 0.7, 1); g.translate(0, 0.55, 0); return g; }
/** A vine: a thin hanging strip with a kink (4 triangles), its top at 0. */
function vineGeo(): THREE.BufferGeometry {
  const w = 0.05, pos = [-w, 0, 0, w, 0, 0, w + 0.1, -0.5, 0, -w, 0, 0, w + 0.1, -0.5, 0, -w + 0.1, -0.5, 0, -w + 0.1, -0.5, 0, w + 0.1, -0.5, 0, w, -1, 0, -w + 0.1, -0.5, 0, w, -1, 0, -w, -1, 0];
  const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.computeVertexNormals(); return g;
}
/** A kind's geometry (person units; a new one each call, the caller's to scale and dispose). */
export function jungleGeometry(kind: JungleKind): THREE.BufferGeometry {
  return kind === 'canopy' ? crownGeo() : kind === 'fern' ? fernGeo() : kind === 'bush' ? bushGeo() : vineGeo();
}

/**
 * The drawn layers' pieces over `trunks` (stand-local, person units), within `radius`: the canopy fill, the understory, the
 * vines. Deterministic in `rnd`.
 */
export function junglePieces(trunks: JungleTrunk[], radius: number, rnd: () => number): JunglePiece[] {
  const n = trunks.length, meanH = trunks.reduce((a, t) => a + t.height, 0) / Math.max(1, n);
  const counts = { canopy: Math.round(n * JUNGLE.canopyPerTrunk), fern: Math.round(n * JUNGLE.understoryPerTrunk * 0.6), bush: Math.round(n * JUNGLE.understoryPerTrunk * 0.4), vine: Math.round(n * JUNGLE.vinesPerTrunk) };
  const out: JunglePiece[] = [], c = new THREE.Color();
  const inDisc = () => { for (;;) { const x = (rnd() * 2 - 1) * radius, z = (rnd() * 2 - 1) * radius; if (x * x + z * z <= radius * radius) return [x, z]; } };
  const shade = (base: string, spread: number) => c.set(base).multiplyScalar(1 - spread / 2 + spread * rnd()).getHex();
  // the canopy fill: crowns between the trunks at the canopy's height, wide and flat, so the cover closes
  for (let i = 0; i < counts.canopy; i++) {
    const [x, z] = inDisc(), w = (3.2 + 2.4 * rnd()) * JUNGLE.crownScale;
    const y = meanH * (JUNGLE.canopyAt + 0.12 * rnd()), yaw = rnd() * Math.PI * 2, sy = w * (0.8 + 0.4 * rnd());
    out.push({ kind: 'canopy', x, y, z, yaw, sx: w, sy, sz: w, color: shade(i % 3 ? JUNGLE_COLORS.leaf : JUNGLE_COLORS.leafDark, 0.25) });
  }
  // the understory: ferns and bushes between the trunks, chest high, kept off the trunks themselves
  const offTrunk = (): [number, number] => { for (let k = 0; k < 8; k++) { const [x, z] = inDisc(); if (trunks.every(t => (t.x - x) ** 2 + (t.z - z) ** 2 > 0.8)) return [x, z]; } return inDisc() as [number, number]; };
  for (let i = 0; i < counts.fern; i++) {
    const [x, z] = offTrunk(), sz = 0.9 + 0.7 * rnd(), yaw = rnd() * Math.PI * 2, sy = sz * (0.9 + 0.5 * rnd());
    out.push({ kind: 'fern', x, y: 0, z, yaw, sx: sz, sy, sz, color: shade(JUNGLE_COLORS.fern, 0.3) });
  }
  for (let i = 0; i < counts.bush; i++) {
    const [x, z] = offTrunk(), sz = 0.7 + 0.6 * rnd(), yaw = rnd() * Math.PI * 2;
    out.push({ kind: 'bush', x, y: 0, z, yaw, sx: sz * 1.2, sy: sz, sz: sz * 1.2, color: shade(JUNGLE_COLORS.bush, 0.3) });
  }
  // the vines: from a crown's underside down most of the way to the ground
  for (let i = 0; i < counts.vine; i++) {
    const t = trunks[Math.floor(rnd() * n)] ?? { x: 0, z: 0, height: meanH }, a = rnd() * Math.PI * 2, r = 0.8 + 1.6 * rnd();
    const top = t.height * (0.7 + 0.1 * rnd()), len = top * (0.45 + 0.4 * rnd()), yaw = rnd() * Math.PI;
    out.push({ kind: 'vine', x: t.x + Math.cos(a) * r, y: top, z: t.z + Math.sin(a) * r, yaw, sx: 1, sy: len, sz: 1, color: shade(JUNGLE_COLORS.vine, 0.3) });
  }
  return out;
}

/** The drawn layers over `trunks` as one group of four instanced meshes (the sheet's): `junglePieces`, laid flat. */
export function jungleLayers(trunks: JungleTrunk[], radius: number, rnd: () => number): JungleLayers {
  const group = new THREE.Group(); group.name = 'jungle';
  const pieces = junglePieces(trunks, radius, rnd);
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), s = new THREE.Vector3(), c = new THREE.Color(), up = new THREE.Vector3(0, 1, 0);
  let triangles = 0;
  for (const kind of JUNGLE_KINDS) {
    const mine = pieces.filter(x => x.kind === kind);
    const mesh = new THREE.InstancedMesh(jungleGeometry(kind), markShared(new THREE.MeshLambertMaterial({ color: 0xffffff, flatShading: true, side: THREE.DoubleSide })), mine.length);
    mesh.name = `jungle:${kind}`;
    mine.forEach((x, i) => { m.compose(p.set(x.x, x.y, x.z), q.setFromAxisAngle(up, x.yaw), s.set(x.sx, x.sy, x.sz)); mesh.setMatrixAt(i, m); mesh.setColorAt(i, c.setHex(x.color)); });
    mesh.instanceMatrix.needsUpdate = true; if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    group.add(mesh);
    const g = mesh.geometry;
    triangles += (g.index ? g.index.count / 3 : g.attributes.position.count / 3) * mesh.count;
  }
  return { group, pieces: pieces.length, draws: 4, triangles };
}
